import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { sendGolfMessengerHistoryAndProfile } from '../utils/messenger';
// import { useNavigate } from 'react-router-dom';

// Define the storage key for anonymous profile
const ANONYMOUS_PROFILE_STORAGE_KEY = 'anonymousUserProfile';

interface Message {
  id: number;
  sender: 'user' | 'ai';
  text: string;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface MessengerRef {
  openMessenger: () => void;
}

const MAX_CONVERSATION_HISTORY = 20;

type MessengerProps = {
  onEditProfile: () => void;
};

const Messenger = forwardRef<MessengerRef, MessengerProps>((props, ref) => {
  // Helper function to keep only the last N messages
  const limitConversationHistory = (messages: ConversationMessage[], limit: number): ConversationMessage[] => {
    return messages.slice(-limit);
  };

  // Load conversation history from localStorage
  const loadConversationHistory = (): ConversationMessage[] => {
    try {
      const saved = localStorage.getItem('golf-messenger-conversation-history');
      const history = saved ? JSON.parse(saved) : [];
      return limitConversationHistory(history, MAX_CONVERSATION_HISTORY);
    } catch {
      return [];
    }
  };

  // Load messages from localStorage or use default
  const loadMessagesFromStorage = (): Message[] => {
    try {
      const saved = localStorage.getItem('messenger-conversation');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure we have at least the welcome message
        if (parsed.length === 0) {
          return [{ id: 1, sender: 'ai', text: 'Hi! How can I help you with your golf fitting today?' }];
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading messages from localStorage:', error);
    }
    return [{ id: 1, sender: 'ai', text: 'Hi! How can I help you with your golf fitting today?' }];
  };

  // Load visibility state from localStorage
  const loadVisibilityFromStorage = (): boolean => {
    try {
      const saved = localStorage.getItem('messenger-visible');
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.error('Error loading visibility from localStorage:', error);
      return false;
    }
  };

  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>(loadConversationHistory);
  const [messages, setMessages] = useState<Message[]>(loadMessagesFromStorage);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(loadVisibilityFromStorage);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // const navigate = useNavigate();

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openMessenger: () => {
      setVisible(true);
    }
  }));

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    try {
      localStorage.setItem('messenger-conversation', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving messages to localStorage:', error);
    }
  }, [messages]);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    try {
      const limitedHistory = limitConversationHistory(conversationHistory, MAX_CONVERSATION_HISTORY);
      localStorage.setItem('golf-messenger-conversation-history', JSON.stringify(limitedHistory));
    } catch (error) {
      console.error('Error saving conversation history to localStorage:', error);
    }
  }, [conversationHistory]);

  // Save visibility state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('messenger-visible', JSON.stringify(visible));
    } catch (error) {
      console.error('Error saving visibility to localStorage:', error);
    }
  }, [visible]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when messenger becomes visible
  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [visible]);

  // Listen for storage changes (like when user signs out)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'messenger-conversation' && e.newValue === null) {
        // Conversation was cleared (user signed out), reset to default
        const defaultMessage = { id: 1, sender: 'ai' as const, text: 'Hi! How can I help you with your golf fitting today?' };
        setMessages([defaultMessage]);
      }
      if (e.key === 'golf-messenger-conversation-history' && e.newValue === null) {
        // Conversation history was cleared, reset to empty
        setConversationHistory([]);
      }
      if (e.key === 'messenger-visible' && e.newValue === null) {
        // Visibility state was cleared, reset to closed
        setVisible(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleSend = async () => {
    if (input.trim() === '' || loading) return;
    const userMsg: Message = {
      id: messages.length + 1,
      sender: 'user',
      text: input.trim(),
    };
    
    // Add user message to conversation history
    const userConversationMsg: ConversationMessage = {
      role: 'user',
      content: input.trim()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    
    try {
      // Create new conversation history with the current user message
      const newConversationHistory = [...conversationHistory, userConversationMsg];
      
      // Call API with full conversation history (limited to last 20 messages)
      const limitedHistory = limitConversationHistory(newConversationHistory, MAX_CONVERSATION_HISTORY);
      let userProfile = localStorage.getItem(ANONYMOUS_PROFILE_STORAGE_KEY) || '{}';

      const aiText = await sendGolfMessengerHistoryAndProfile(limitedHistory, userProfile);

      // Add AI response to conversation history
      const aiConversationMsg: ConversationMessage = {
        role: 'assistant',
        content: aiText
      };
      
      // Update conversation history with both user and AI messages, and limit to 20
      const updatedHistory = limitConversationHistory([...newConversationHistory, aiConversationMsg], MAX_CONVERSATION_HISTORY);
      setConversationHistory(updatedHistory);
      
      setMessages(prev => [
        ...prev,
        {
          id: prev.length + 1,
          sender: 'ai',
          text: aiText,
        },
      ]);
    } catch (e) {
      setMessages(prev => [
        ...prev,
        {
          id: prev.length + 1,
          sender: 'ai',
          text: 'Sorry, there was a problem connecting to the golf assistant.',
        },
      ]);
    } finally {
      setLoading(false);
      // Refocus the input field after the operation is complete
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSend();
  };

  // const clearConversation = () => {
  //   const defaultMessage = { id: 1, sender: 'ai' as const, text: 'Hi! How can I help you with your golf fitting today?' };
  //   setMessages([defaultMessage]);
  //   setConversationHistory([]);
  //   localStorage.setItem('messenger-conversation', JSON.stringify([defaultMessage]));
  //   localStorage.setItem('golf-messenger-conversation-history', JSON.stringify([]));
  // };

  const handleEditProfile = () => {
    // Navigate to Home and trigger the General tab
    props.onEditProfile();
  };

  return (
    <div style={{ float: 'right' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {typeof navigator !== "undefined" && /iPhone/i.test(navigator.userAgent) && (
          <button onClick={handleEditProfile} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <span
              style={{
                background: '#fff',
                color: '#1976d2',
                borderRadius: '12px',
                border: '2px solid #1976d2',
                padding: '6px',
                marginRight: '35px',
                position: 'relative',
                fontWeight: 600,
                fontSize: '1rem',
                top: '4px', // move down by 4px
                whiteSpace: 'nowrap',
                display: 'none'
              }}
            >
              Edit Profile
            </span>
          </button>
        )}
        <button
          onClick={() => {
            setVisible(v => {
              if (v) {
                // fix iphone zoom out issue
                window.scrollTo(0, 0);
              }
              return !v;
            });
          }}
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            border: 'none',
            background: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            gap: '0.5em',
            width: 'fit-content',
            margin: 0,
          }}
          aria-label={visible ? 'Hide Messenger' : 'Show Messenger'}
          title={visible ? 'Hide Messenger' : 'Show Messenger'}
        >
          {visible ? '' : (
            <span>
              {typeof navigator !== "undefined" && !/iPhone/i.test(navigator.userAgent) && (
            <span>
              <span
                style={{
                  background: '#fff',
                  color: '#1976d2',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
                  border: '2px solid #1976d2',
                  padding: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ marginRight: '4px' }}>Ask NeutrAI Assistant</span>
              </span>
              <span
                role="img"
                aria-label="chat"
                style={{
                  background: '#1976d2',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 44,
                  height: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                }}
              >
                💬
              </span>
            </span>
            )}
            </span>
          )}
        </button>
      </div>
      {visible && (
        <div
          className="messenger-container"
          style={{
            marginTop: 41,
            marginRight: -14,
            width: '50%',         // messenger width in desktop or ipad view
            height: '80%',        // messenger height in desktop or ipad view
            maxWidth: '95vw',
            maxHeight: '95vh',
            background: '#fff',
            borderRadius: 16,
            border: '1.5px solid #1976D2',
            boxShadow: '0 4px 24px rgba(25, 37, 210, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'fixed',
            right: 32,
            bottom: 12,
            zIndex: 1200
          }}
        >
          <div className="messenger-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="messenger-title" style={{padding: '0 1.2em', margin: 0, flex: 1}}>NeutrAI Assistant</h3>
            <button
              className="messenger-toggle-btn"
              onClick={() => {
                setVisible(v => {
                  if (v) {
                    // fix iphone zoom out issue
                    window.scrollTo(0, 0);
                  }
                  return !v;
                });
              }}
              style={{
                position: 'absolute',
                top: visible ? 10 : -35,
                right: 4,
                zIndex: 1100,
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: visible ? 32 : 54,
                height: visible ? 32 : 54,
                fontSize: visible ? 20 : 32,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                transition: 'background 0.2s, width 0.2s, height 0.2s, font-size 0.2s, top 0.2s, right 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label={visible ? 'Hide Messenger' : 'Show Messenger'}
              title={visible ? 'Hide Messenger' : 'Show Messenger'}
            >
              {visible ? '×' : '.'}
            </button>
          </div>
          <div className="messenger-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`messenger-message messenger-message-${msg.sender}`}>
                {msg.text}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="messenger-input-row">
            <input
              ref={inputRef}
              type="text"
              className="messenger-input"
              placeholder="Type your message..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={loading}
            />
            <button className="messenger-send-btn" onClick={handleSend} disabled={!input.trim() || loading}>
              {loading ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default Messenger;
